import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Link from 'next/link';
import React, { useRef } from 'react';
import { useOutsideClick } from '../../../../../utils/hooks/useOutsideClick';
import { breakpoints, colors, spacing } from '../../../../../utils/styleguide';
import { MAX_WIDTH } from '../../../layout/12ColGridLayout';
import Typography from '../../../text/Typography';
import { NavItemType } from '../../navTypes';

const NAV_BAR_SIDE_PADDING = spacing[10];

const SubmenuWrapper = styled.div`
  background: ${colors.brightGrey};
  position: fixed;
  top: ${spacing[10]}px;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  max-width: ${MAX_WIDTH - NAV_BAR_SIDE_PADDING}px;
  width: inherit;
  box-sizing: border-box;
  ${breakpoints.desktop} {
    padding: 0 ${spacing[6]}px;
  }
`;

const LinkWrapper = css`
  flex: 1;
  min-width: 25%;
  cursor: pointer;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  padding: ${spacing[6]}px ${spacing[4]}px;
  background: ${colors.brightGrey};
  &:hover {
    background: ${colors.yellow};
  }
  img {
    height: 104px;
    width: 104px;
    object-fit: contain;
    margin-bottom: ${spacing[5]}px;
  }
`;

const LinkText = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
  width: 100%;
  text-decoration: none;
  margin-bottom: ${spacing[3]}px;
`;

const DescriptionText = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
  width: 100%;
  text-decoration: none;
`;

type SubmenuProps = {
  links?: NavItemType[] | undefined;
  clickOutside: (event: MouseEvent) => void;
  onLinkClicked: () => void;
};

const Submenu = ({ links, clickOutside, onLinkClicked }: SubmenuProps) => {
  const submenuRef = useRef(null);
  useOutsideClick(submenuRef, clickOutside);
  if (!links) return null;
  return (
    <SubmenuWrapper ref={submenuRef}>
      {links.map(({ title, image, href, description }: NavItemType, index) => {
        const content = (
          <>
            {image}
            <LinkText>
              <Typography variant="buttonLarge" tag="p">
                {title}
              </Typography>
            </LinkText>
            <DescriptionText>
              <Typography variant="smallText" tag="p">
                {description}
              </Typography>
            </DescriptionText>
          </>
        );
        return title ? (
          <Link href={href as string} key={index}>
            <a href={href} aria-label={title} css={LinkWrapper} onClick={onLinkClicked}>
              {content}
            </a>
          </Link>
        ) : (
          <div key={index} css={LinkWrapper}>
            {content}
          </div>
        );
      })}
    </SubmenuWrapper>
  );
};

export default Submenu;
